import { type ChangeEvent, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { Skeleton } from "~/atoms/skeleton";
import { Label } from "~/atoms/label";
import { Input } from "~/atoms/input";
import { Textarea } from "~/atoms/textarea";
import { WorkflowBuilder } from "~/organisms/WorkflowBuilder";
import {
  type WorkflowStepInput,
  type WorkflowTemplate,
  type WorkflowDetails,
} from "~/api/types";
import { DocumentVersionStatus } from "~/config/types";
import { mapStepInputsToWorkflowSteps } from "~/helpers/workflow";

interface Props {
  template: WorkflowTemplate;
  editedSteps: WorkflowStepInput[];
  setEditedSteps: Dispatch<SetStateAction<WorkflowStepInput[]>>;
  editedName: string;
  onNameChange: (name: string) => void;
  editedDescription: string | null;
  onDescriptionChange: (description: string | null) => void;
  setHasUnsavedChanges: (value: boolean) => void;
  actions?: React.ReactNode;
}

const DUMMY_DOCUMENT_VERSION = {
  id: 0,
  title: "",
  status: DocumentVersionStatus.draft,
  fileType: "docx" as const,
  reviewTo: null,
  createdAt: new Date(0).toISOString(),
  publishedAt: null,
  permissions: {
    canRead: true,
    canWrite: true,
    canManage: true,
    canPublish: true,
  },
};

const DUMMY_DOCUMENT = {
  id: 0,
  category: { id: 0, title: "" },
  owner: { userId: 0 },
  scheduleReviewFrom: null,
};

function buildWorkflowDetailsForTemplate(
  template: WorkflowTemplate,
  steps: WorkflowStepInput[],
): WorkflowDetails {
  return {
    id: template.id,
    isOngoing: false,
    steps: mapStepInputsToWorkflowSteps(steps),
    description: template.description,
    workflowTemplateId: template.id,
    workflowTemplateName: template.name,
    documentVersion: DUMMY_DOCUMENT_VERSION,
    document: DUMMY_DOCUMENT,
    stepInstances: [],
  };
}

export function WorkflowTemplateEditor({
  template,
  editedSteps,
  setEditedSteps,
  editedName,
  onNameChange,
  editedDescription,
  onDescriptionChange,
  setHasUnsavedChanges,
  actions,
}: Props) {
  const { t } = useTranslation();

  const workflowDetails = buildWorkflowDetailsForTemplate(
    template,
    editedSteps,
  );

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    onNameChange(e.target.value);
  };

  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onDescriptionChange(e.target.value || null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("workflowTemplates.templateName")}</CardTitle>
        <Input
          value={editedName}
          onChange={handleNameChange}
          placeholder={t("workflowTemplates.templateNamePlaceholder")}
        />
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <Label className="text-base font-semibold">
            {t("commons.workflow.description")}
          </Label>
          <p className="text-muted-foreground text-sm">
            {t("commons.workflow.descriptionDescription")}
          </p>
          <Textarea
            value={editedDescription ?? ""}
            onChange={handleDescriptionChange}
            className="h-52"
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-base font-semibold">
            {t("commons.workflow.steps")}
          </Label>
          <p className="text-muted-foreground text-sm">
            {t("commons.workflow.stepsDescription")}
          </p>
          <WorkflowBuilder
            isEditMode
            hideDueDates
            workflow={workflowDetails}
            steps={editedSteps}
            setSteps={setEditedSteps}
            setHasUnsavedChanges={setHasUnsavedChanges}
          />
        </div>
      </CardContent>
      {actions && (
        <CardFooter className="justify-end gap-2">{actions}</CardFooter>
      )}
    </Card>
  );
}

export function WorkflowTemplateEditorSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-9 w-full" />
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-52 w-full" />
        </div>
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </CardContent>
      <CardFooter className="justify-end gap-2">
        <Skeleton className="h-9 w-28" />
        <Skeleton className="h-9 w-28" />
      </CardFooter>
    </Card>
  );
}
